// Tipos para la aplicación de Divertilandia

// Tipos para Cláusulas de Juguetes
export interface ToyClause {
  id: string;
  name: string;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  games?: Game[]; // Relación con juegos
}

export interface Package {
  id: string;
  name: string;
  description: string;
  price: number;
  image_base64?: string; // Imagen en base64
  max_age: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  games?: PackageGame[]; // Relación con juegos
}

export interface PackageGame {
  id: string;
  name: string;
  toy_type?: ToyType;
  quantity: number;
  unit_price: number;
  condition: GameCondition;
  image_base64?: string;
  is_active: boolean;
  // Relación con cláusulas
  toy_clauses?: ToyClause[];
  pivot: {
    quantity: number; // Cantidad específica en el paquete
  };
}

// Tipos para Gastos
export interface ExpenseCategory {
  id: string;
  name: string;
  description?: string;
  color: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Expense {
  id: string;
  concept: string;
  description?: string;
  amount: number;
  receipt_image?: string;
  expense_category_id: string;
  event_id?: string;
  status: 'pending' | 'paid';
  expense_date: string;
  payment_date?: string;
  created_at: string;
  updated_at: string;
  category?: ExpenseCategory;
  event?: Event;
  formatted_amount?: string;
  status_formatted?: string;
}

// Tipos para Vehículos
export interface Vehicle {
  id: string;
  name: string;
  model?: string;
  year?: string;
  plate_number?: string;
  fuel_type: 'regular' | 'premium' | 'diesel';
  vehicle_type?: 'regular' | 'hybrid';
  fuel_efficiency: number; // km/litro
  battery_capacity_kwh?: number; // Capacidad de batería en kWh (solo híbridos)
  electric_range_km?: number; // Kilómetros en modo 100% eléctrico (solo híbridos)
  battery_min_percentage?: number; // Porcentaje mínimo de batería (default 25%)
  hybrid_efficiency_km_per_liter?: number; // Rendimiento combinado PHEV en km/L (solo híbridos)
  color: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  fuel_type_formatted?: string;
  cost_per_kilometer?: number;
  current_fuel_price?: number; // precio actual obtenido dinámicamente
}

// Tipos para Uso de Vehículos en Eventos
export interface EventVehicleUsage {
  id: string;
  event_id: string;
  vehicle_id: string;
  kilometers: number;
  initial_battery_percentage?: number | null; // Porcentaje de batería inicial (solo híbridos)
  fuel_cost: number;
  status: 'pending' | 'paid';
  notes?: string;
  created_at: string;
  updated_at: string;
  event?: Event;
  vehicle?: Vehicle;
  formatted_fuel_cost?: string;
  status_formatted?: string;
}

export interface FuelPrice {
  id: string;
  fuel_type: 'regular' | 'premium' | 'diesel' | 'electricity';
  price_per_liter: number; // Para electricidad, este campo representa precio por kWh
  effective_date: string;
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
  fuel_type_formatted?: string;
  formatted_price?: string;
}

export interface Game {
  id: string;
  name: string;
  toy_type_id: string;
  toy_type?: ToyType; // Relación con el tipo de juguete
  quantity: number;
  unit_price: number;
  condition: GameCondition;
  image_base64?: string; // Imagen en base64
  is_active: boolean;
  // Contadores por estado de salud
  excellent_count?: number;
  good_count?: number;
  fair_count?: number;
  poor_count?: number;
  broken_count?: number;
  // Contadores de disponibilidad
  available_count?: number;
  in_use_count?: number;
  maintenance_count?: number;
  retired_count?: number;
  // Relación con cláusulas
  toy_clauses?: ToyClause[];
}

// Interfaz para los tipos de juguetes (CRUD) - Según backend
export interface ToyType {
  id: string;
  name: string;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export enum GameType {
  INFLATABLE = 'inflatable',
  MECHANICAL = 'mechanical',
  BOARD_GAME = 'board_game',
  SPORTS = 'sports',
  CREATIVE = 'creative',
  ELECTRONIC = 'electronic'
}

export enum GameCondition {
  EXCELLENT = 'excellent',
  GOOD = 'good',
  FAIR = 'fair',
  NEEDS_REPAIR = 'needs_repair',
  OUT_OF_SERVICE = 'out_of_service'
}

export interface Event {
  id: string;
  // Información de contacto
  contact_name: string;
  contact_phone: string;
  secondary_phone?: string;
  
  // Ubicación
  address: string;
  google_maps_url?: string;
  latitude?: number;
  longitude?: number;
  
  // Fecha y hora del evento
  event_date: string; // YYYY-MM-DD format
  start_time: string; // HH:MM format
  end_time?: string; // HH:MM format - calculado automáticamente
  
  // Duración y horas extras
  extra_hours: number; // 0, 1, 2, o 3 horas extras
  extra_hours_cost: number; // Costo total de horas extras
  
  // Paquete y anticipo
  package_id: string;
  package?: Package; // Relación con el paquete
  discount_percentage?: number; // Porcentaje de descuento sobre el paquete (0-100)
  package_discount_amount?: number; // Monto del descuento en pesos
  advance_payment: number; // Anticipo fijo de $300
  
  // Nuevos campos para extras
  has_advance_payment: boolean;
  advance_payment_amount?: number;
  extra_hours_count: number;
  extra_tables: number;
  extra_chairs: number;
  extra_playpens: number; // Corralitos
  extra_toys: number;
  extra_services: string; // Servicios adicionales a consideración
  extra_services_cost?: number;
  
  // Costos de extras
  tables_cost: number; // $100 c/u
  chairs_cost: number; // $100 c/u
  playpens_cost: number; // $200 por cada 8
  toys_cost: number; // A consideración
  total_extras_cost: number;
  
  // Estado del evento
  status: EventStatus;
  
  // Información del niño
  child_gender?: 'male' | 'female';
  
  // Notas adicionales
  notes?: string;
  
  // Timestamps
  created_at: string;
  updated_at: string;
}

export enum EventStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

// Sistema de inventario simplificado usando la tabla games
export interface InventoryItem {
  id: string;
  name: string;
  toy_type_id: string;
  toy_type?: ToyType;
  quantity: number;
  unit_price?: number;
  condition?: string;
  image_base64?: string;
  is_active: boolean;
  
  // Contadores por estado de salud
  excellent_count: number;
  good_count: number;
  fair_count: number;
  poor_count: number;
  broken_count: number;
  
  // Contadores por estado de disponibilidad
  available_count: number;
  in_use_count: number;
  maintenance_count: number;
  retired_count: number;
  
  // Conteos calculados (accessors del modelo)
  health_status_counts?: {
    excellent: number;
    good: number;
    fair: number;
    poor: number;
    broken: number;
  };
  availability_status_counts?: {
    available: number;
    in_use: number;
    maintenance: number;
    retired: number;
  };
  available_units?: number;
  in_use_units?: number;
  maintenance_units?: number;
  retired_units?: number;
  
  created_at: string;
  updated_at: string;
}

export enum HealthStatus {
  EXCELLENT = 'excellent',
  GOOD = 'good',
  FAIR = 'fair',
  POOR = 'poor',
  BROKEN = 'broken'
}

export enum AvailabilityStatus {
  AVAILABLE = 'available',
  IN_USE = 'in_use',
  MAINTENANCE = 'maintenance',
  RETIRED = 'retired'
}

export interface DashboardStats {
  totalEvents: number;
  pendingEvents: number;
  completedEvents: number;
  totalRevenue: number;
  monthlyRevenue: number;
  availableGames: number;
  gamesInMaintenance: number;
}

// Configuraciones del sistema para eventos
export interface EventSettings {
  id: string;
  setting_key: string;
  setting_name: string;
  setting_value: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Formulario para crear/editar eventos
export interface EventFormData {
  contact_name: string;
  contact_phone: string;
  secondary_phone?: string;
  address: string;
  google_maps_url?: string;
  latitude?: number;
  longitude?: number;
  event_date: string;
  start_time: string;
  end_time?: string; // HH:MM format - calculado automáticamente
  extra_hours: number;
  package_id: string;
  discount_percentage?: number; // Porcentaje de descuento sobre el paquete (0-100)
  status: EventStatus;
  child_gender?: 'male' | 'female';
  notes?: string;
  
  // Campos de extras
  has_advance_payment: boolean;
  advance_payment_amount?: number;
  extra_hours_count: number;
  extra_tables: number;
  extra_chairs: number;
  extra_playpens: number;
  extra_toys: number;
  extra_services: string;
  extra_services_cost?: number;
}
